<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaIconNotification class="notification-dropdown__icon" />
        <VaBadge
          v-if="notificationsStore.unreadCount > 0"
          class="notification-dropdown__badge"
          :text="String(notificationsStore.unreadCount)"
          color="danger"
        />
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="p-4 notification-content">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-semibold text-base">Notifications</h3>
          <VaButton
            v-if="notificationsStore.unreadCount > 0"
            preset="secondary"
            size="small"
            @click="handleMarkAllRead"
          >
            Mark all as read
          </VaButton>
        </div>

        <div v-if="notificationsStore.loadingInbox" class="text-sm text-secondary py-2">Loading…</div>
        <div v-else-if="notificationsStore.inboxItems.length === 0" class="text-sm text-secondary py-2">
          No notifications yet.
        </div>

        <div v-else class="space-y-2 max-h-[360px] overflow-y-auto pr-1">
          <div
            v-for="item in notificationsStore.inboxItems"
            :key="item.id"
            class="notification-item"
            :class="{ 'notification-item--unread': !item.isRead }"
          >
            <div class="flex items-start justify-between gap-2">
              <div>
                <p class="font-semibold text-sm">{{ item.title }}</p>
                <p class="text-xs text-secondary mt-1">{{ item.message }}</p>
                <p class="text-[11px] text-secondary mt-1">{{ formatDate(item.createdAt) }}</p>
              </div>
              <VaButton
                v-if="!item.isRead"
                preset="plain"
                size="small"
                class="whitespace-nowrap"
                @click="handleMarkRead(item.id)"
              >
                Mark read
              </VaButton>
            </div>
          </div>
        </div>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { formatDistanceToNow } from 'date-fns'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import { useNotificationsStore } from '../../../../stores/notifications'

const notificationsStore = useNotificationsStore()

const handleMarkRead = async (id: string) => {
  await notificationsStore.markRead(id)
}

const handleMarkAllRead = async () => {
  await notificationsStore.markAllRead()
}

const formatDate = (value: string) => {
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true })
  } catch {
    return value
  }
}

onMounted(() => {
  notificationsStore.loadUnreadCount()
  notificationsStore.loadInbox({ page: 1, pageSize: 10 }).catch(() => undefined)
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .notification-dropdown__badge {
    position: absolute;
    top: -6px;
    right: -8px;
    font-size: 10px;
    min-width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}

.notification-content {
  min-width: 320px;
}

.notification-item {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
}

.notification-item--unread {
  border-color: rgba(37, 99, 235, 0.4);
  background: rgba(219, 234, 254, 0.25);
}
</style>
